/**
 * SunoActionsSheet — единая шторка/поповер-hub с действиями над треком.
 *
 * Заменяет три отдельных секции в CardModal (быстрые действия / параметрические /
 * продвинутые), которые визуально дробили экран и путали юзера. Теперь — один
 * primary CTA «Работать с треком» в карточке, клик → эта шторка с тремя чётко
 * размеченными группами.
 *
 * Адаптивный UI:
 *   - Мобилка (< 768px)  — center Sheet (fullscreen-ish)
 *   - Десктоп + anchorEl — floating popover около кнопки «Улучшить»
 *   - Десктоп без anchor — fallback на Sheet (для CardModal и widget-триггера)
 *
 * Группы:
 *   1. Быстро (one-click)      — результат-файл или картинка за 1 клик
 *   2. Новая версия             — открывает виджет с pin'ом для доп. параметров
 *   3. Редактирование           — специальные UI (waveform, picker)
 *   4. Опасная зона             — удаление трека (с подтверждением)
 *
 * Вложенные sheet'ы (Replace Section / Mashup) открываются поверх через portal.
 * Result-viewer (для stems/midi/timestamped) тоже через portal — без конфликтов.
 */
import { useEffect, useMemo, useState, useCallback } from 'react'
import { useCreditsGuard } from '../../../hooks/useCreditsGuard'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import Sheet from '../../../components/Sheet'
import AnchoredPopover, { useIsDesktopPopover } from '../../../components/AnchoredPopover'
import SpotlightTour from '../../../components/SpotlightTour'
import SideTooltip from '../../../components/SideTooltip'
import TokenPrice from '../../../components/TokenPrice'
import { opAvailable, providerOf } from '../utils/trackOperations'
import { FolderPickerList, type FolderPickerAPI, type FolderPickerLabels } from '../../../components/FolderPicker'
import {
	addTracksToAlbum,
	createAlbum,
	getUserAlbums,
	removeTrackFromAlbum,
	type SunoAlbum,
} from '../../../api/albums'
import { Icon } from '../../../../public/icons/Icon.tsx'
import {
	buildActionsSheetTourSteps,
	SUNO_ACTIONS_SHEET_TOUR_KEY,
} from '../../../data/widgetTourSteps'
import SunoResultViewer from './SunoResultViewer'
import SunoReplaceSectionSheet from './SunoReplaceSectionSheet'
import SunoMashupSheet from './SunoMashupSheet'
import { useSunoTrackActions, type SunoActionKey, type SunoActionDef } from '../hooks/useSunoTrackActions'
import DescribeModal from '../../bots/components/DescribeModal'
import { useDescribe } from '../../bots/components/useDescribe'
import { useSunoWavJobs } from '../../../context/SunoWavJobsContext'
import { useSunoMidiJobs } from '../../../context/SunoMidiJobsContext'
import { useSunoVideoJobs } from '../../../context/SunoVideoJobsContext'
import { useSunoTimestampedJobs } from '../../../context/SunoTimestampedJobsContext'
import { useSunoStemJobs } from '../../../context/SunoStemJobsContext'
import { useSunoPersonas } from '../../../context/SunoPersonasContext'
import { deleteTrack } from '../../../api/music'
import { showToast } from '../../../components/Toast'
import type { TaskStatusResponse } from '../../../api/images'
import type { SunoTrack } from '../../../api/music'
import type { AudioOperation } from '../../../context/GenerateContext'

interface Props {
	open: boolean
	onClose: () => void
	track: SunoTrack
	/** Триггер параметрической операции (открывает виджет + ставит pin). */
	onTrackParametricAction?: (track: SunoTrack, operation: AudioOperation) => void
	/** После успешного one-click / параметрической — обновить список. */
	onCompleted?: () => void
	/** Открыть инлайн-редактор обложки трека. Если не передан — fallback на
	 *  переход на страницу инструмента обложек (старое поведение). */
	onEditCover?: (track: SunoTrack) => void
	/** Кэш треков из библиотеки для Mashup picker'а (без повторного fetch). */
	tracksCache?: SunoTrack[]
	/** Anchor-элемент на десктопе для popover-режима. На мобилке игнорируется,
	 *  рендерится Sheet. Без anchor на десктопе — тоже Sheet (fallback для
	 *  CardModal и widget-триггера где кнопки нет). */
	anchorEl?: HTMLElement | null
	/** После успешного удаления трека — parent должен убрать его из списка. */
	onDeleted?: (track: SunoTrack) => void
	// ─── Папки (организация трека) ─────────────────────────────────
	// Когда передано — отображается группа «Папки» с accordion «Добавить
	// в папку» + опционально «Убрать из «X»» (на странице папки).
	// Без этих пропсов (из CardModal) группа Папки не рендерится.
	/** Lookup: в каких альбомах уже состоит трек — из SunoTrack.album_ids
	 *  у parent'а. Нужно для initial state чекбоксов в AddToAlbumList. */
	getTrackAlbums?: (trackId: string) => string[]
	/** После add/remove/create альбома — parent обновляет свои данные. */
	onAlbumsChanged?: () => void
	/** Клик по «Убрать из «X»». Только на странице конкретной папки. */
	onRemoveFromCurrentAlbum?: (track: SunoTrack) => void
	/** Название текущей папки — для лейбла «Убрать из «...»». */
	currentAlbumName?: string
}

const POPOVER_WIDTH = 380 // шире default 320 — в sheet много контента

function pluralizeAlbums(n: number): string {
	const mod10 = n % 10
	const mod100 = n % 100
	if (mod10 === 1 && mod100 !== 11) return 'папке'
	if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'папках'
	return 'папках'
}

interface ParametricDef {
	operation: Exclude<AudioOperation, 'generate'>
	label: string
	description: string
	variantKey: string
}

const PARAMETRIC_ACTIONS: ParametricDef[] = [
	{ operation: 'extend', label: 'Продлить', description: 'Добавить продолжение к треку', variantKey: 'extend' },
	{ operation: 'cover', label: 'Cover', description: 'Перепоёт в другом жанре', variantKey: 'upload_cover' },
	{ operation: 'add_vocals', label: '+ Вокал', description: 'К инструменталу добавить голос', variantKey: 'add_vocals' },
	{ operation: 'add_instrumental', label: '+ Инструментал', description: 'К вокалу добавить аккомпанемент', variantKey: 'add_instrumental' },
]

const VIEWER_ACTIONS: SunoActionKey[] = ['stems', 'stem_minus', 'midi', 'timestamped', 'video']

export default function SunoActionsSheet({
	open,
	onClose,
	track,
	onTrackParametricAction,
	onCompleted,
	onEditCover,
	tracksCache,
	anchorEl,
	onDeleted,
	getTrackAlbums,
	onAlbumsChanged,
	onRemoveFromCurrentAlbum,
	currentAlbumName,
}: Props) {
	const guard = useCreditsGuard()
	const [viewer, setViewer] = useState<{ key: SunoActionKey; result: TaskStatusResponse } | null>(null)
	const [replaceOpen, setReplaceOpen] = useState(false)
	const [mashupOpen, setMashupOpen] = useState(false)
	// Удаление трека: двухстадийное (кнопка → confirm-шаг с «Да/Отмена»)
	// чтобы случайный клик не сносил трек навсегда.
	const [confirmDelete, setConfirmDelete] = useState(false)
	const [deleting, setDeleting] = useState(false)
	// Tour: 5 шагов-описаний групп (Папки / Быстро / Новая версия /
	// Редактирование / Удалить). Авто-показ на первом открытии + перезапуск.
	const [tourOpen, setTourOpen] = useState(false)
	// SubView — accordion для «Добавить в папку». 'menu' = обычный набор
	// всех групп, 'addToAlbum' = только FolderPickerList + кнопка «Назад».
	// Переключается кликом по «Добавить в папку» внутри группы Папки.
	const [subView, setSubView] = useState<'menu' | 'addToAlbum'>('menu')
	const navigate = useNavigate()

	// Generic FolderPicker API-адаптер для аудио-альбомов (accordion-шаг).
	const albumPickerAPI = useMemo<FolderPickerAPI<SunoAlbum>>(
		() => ({
			list: async () => ({ folders: (await getUserAlbums()).albums }),
			create: async (name) => {
				const res = await createAlbum({ name })
				return { ok: res.ok, folder: res.album, error: res.error }
			},
			addItems: async (fid, tids) => {
				const res = await addTracksToAlbum(fid, tids)
				return { ok: res.ok }
			},
			removeItem: async (fid, tid) => {
				const res = await removeTrackFromAlbum(fid, tid)
				return { ok: res.ok }
			},
			getFolderIdsForItem: getTrackAlbums ?? (() => []),
			getFolderItemCount: (a) => a.track_count,
		}),
		[getTrackAlbums],
	)

	const albumPickerLabels: FolderPickerLabels = useMemo(() => ({
		createTitle: 'Создать альбом',
		addSingleTitle: 'Добавить в альбом',
		addMultiTitle: (n) => `Добавить ${n} треков в альбом`,
		inputPlaceholder: 'Название альбома',
		createButtonLabel: 'Создать новый альбом',
		emptyText: 'Ещё нет альбомов. Создай первый выше.',
	}), [])
	// Сбрасываем subView при закрытии — чтобы при следующем открытии юзер
	// видел menu, а не addToAlbum которое осталось с прошлой сессии.
	useEffect(() => {
		if (!open) setSubView('menu')
	}, [open])

	const showFoldersGroup = !!(getTrackAlbums && onAlbumsChanged)

	const isDesktop = useIsDesktopPopover()
	const usePopover = isDesktop && !!anchorEl

	// Авто-показ тура при первом открытии (любой режим — popover/sheet).
	// 400ms delay чтобы контент успел отрендериться в DOM (для popover —
	// портал рендерится асинхронно).
	useEffect(() => {
		if (!open) return
		if (typeof window === 'undefined') return
		if (window.localStorage.getItem(SUNO_ACTIONS_SHEET_TOUR_KEY)) return
		const t = setTimeout(() => setTourOpen(true), 400)
		return () => clearTimeout(t)
	}, [open])

	const handleTourFinish = useCallback(() => {
		try {
			window.localStorage.setItem(SUNO_ACTIONS_SHEET_TOUR_KEY, '1')
		} catch {
			// localStorage может быть отключён — тур просто покажется снова
		}
		setTourOpen(false)
	}, [])

	const handleRestartTour = useCallback(() => {
		setTourOpen(true)
	}, [])

	const handleDeleteTrack = async () => {
		if (deleting) return
		setDeleting(true)
		try {
			const res = await deleteTrack(track.id)
			if (!res.ok) throw new Error(res.error || 'Не удалось удалить')
			showToast('Трек удалён', 'success')
			onDeleted?.(track)
			onClose()
		} catch (e) {
			showToast(e instanceof Error ? e.message : 'Не удалось удалить трек', 'error')
			setDeleting(false)
		}
	}

	const { actions, activeKey, isRunning, runAction, priceOf } = useSunoTrackActions({
		onCompleted,
		// Цены операций — по провайдеру трека (Mureka-трек → Mureka-цены из админки).
		provider: providerOf(track.model),
		model: track.model, // точная цена по версии модели трека (extend у Mureka разный)
		onResult: (action, result) => {
			if (VIEWER_ACTIONS.includes(action.key)) {
				setViewer({ key: action.key, result })
			}
		},
	})
	const { describeData, describeBusy, runDescribe, closeDescribe } = useDescribe()

	// WAV рулится отдельным контекстом App-уровня — чтобы polling пережил
	// закрытие шторки. Также даёт localStorage-кэш wav_url для UI fallback
	// пока backend не деплой ещё не отдаёт suno_tracks.wav_url в API.
	const wavJobs = useSunoWavJobs()
	const wavIsRunning = wavJobs.isRunning(track.id)
	const wavCached = wavJobs.resolveWav(track)

	// Обложка трека вынесена в единый редактор (TrackCoverSheet/ImageStudioSheet,
	// вход «Обложка трека») — Suno-натив через coverJobs живёт уже там.

	// MIDI — аналогично: persistent polling в App-context, lightweight viewer
	// открывается автоматически через pendingViewer.
	const midiJobs = useSunoMidiJobs()
	const midiIsRunning = midiJobs.isRunning(track.id)
	const midiCached = midiJobs.getCached(track.id)

	// Video — music-video генерация. Занимает 3-5 мин, поэтому тоже на уровне
	// App — юзер может закрыть sheet и получить уведомление когда готово.
	const videoJobs = useSunoVideoJobs()
	const videoIsRunning = videoJobs.isRunning(track.id)
	const videoCached = videoJobs.getCached(track.id)

	// Timestamped (Lyrics+) — аналогично: App-level polling + karaoke-viewer.
	const timestampedJobs = useSunoTimestampedJobs()
	const timestampedIsRunning = timestampedJobs.isRunning(track.id)
	const timestampedCached = timestampedJobs.getCached(track.id)

	// Personas — job-based создание голосового профиля.
	const personas = useSunoPersonas()
	const personaIsCreating = personas.isCreating(track.id)

	// Stems — App-level джоб (пилюля + persistent-поллинг + вьюер дорожек).
	const stemJobs = useSunoStemJobs()
	const stemsIsRunning = stemJobs.isRunning(track.id)

	const handleQuickAction = (a: SunoActionDef) => {
		// WAV рулится отдельно через App-контекст — чтобы polling пережил
		// закрытие шторки. Аналогично Cover.
		if (a.key === 'wav') {
			wavJobs.start(track)
			onClose()
			return
		}
		if (a.key === 'midi') {
			midiJobs.start(track)
			onClose() // viewer откроется сам когда готово / cached
			return
		}
		if (a.key === 'video') {
			videoJobs.start(track)
			onClose() // viewer откроется сам когда готово / cached; toast на старте
			return
		}
		if (a.key === 'timestamped') {
			timestampedJobs.start(track)
			onClose() // karaoke-viewer откроется сам при готовности
			return
		}
		if (a.key === 'persona') {
			personas.start(track)
			onClose() // toast появится сам + список обновится
			return
		}
		if (a.key === 'stems' || a.key === 'stem_minus') {
			// Ищем ГОТОВЫЙ результат нужного вида и открываем его; платим только
			// если такого ещё нет. Флаг has_stems для этого не годится — он один
			// на оба вида, и «Дорожки» открывали бы минусовку.
			void stemJobs.openOrStart(track, a.key === 'stem_minus' ? 'minus' : 'stems')
			onClose() // пилюля на треке + вьюер дорожек откроется по готовности
			return
		}
		if (isRunning) return
		runAction(a, track.id)
	}

	const handleParametric = (op: ParametricDef) => {
		if (!onTrackParametricAction) return
		onTrackParametricAction(track, op.operation)
		onClose()
	}

	const hasAudio = !!(track.audio_url || track.stream_audio_url)
	const provider = providerOf(track.model)

	// ─── Тело экшенов — одинаковое в Sheet и Popover ────────────────
	const body = (
		<div
			className={
				'suno-actions-sheet__body' +
				(usePopover ? ' suno-actions-sheet__body--popover' : '')
			}
		>
			{/* Help-кнопка `?` — перезапуск тура. Абсолютно позиционируется
			    в top-right body (.suno-actions-sheet__help) чтобы работала
			    одинаково в popover и sheet режимах (в popover нет Sheet-
			    header'а с headerExtra). */}
			<button
				type="button"
				className="suno-actions-sheet__help"
				onClick={handleRestartTour}
				aria-label="Как пользоваться операциями над треком"
				title="Как пользоваться"
			>
				?
			</button>

			<div className="suno-actions-sheet__track">
				<Icon id="gen-audio" size={14} />
				<span className="suno-actions-sheet__track-title">
					{track.title?.trim() || 'Без названия'}
				</span>
			</div>

			{/* ── SubView 'addToAlbum' — accordion-шаг «Добавить в папку» ──
			    Заменяет весь контент menu'а (backarrow + AddToAlbumList).
			    Флоу аналогичен старому TrackFolderMenuSheet, но внутри
			    общего SunoActionsSheet чтобы не было вложенных popover'ов. */}
			{subView === 'addToAlbum' && showFoldersGroup && (
				<>
					<div className="suno-actions-sheet__folders-header">
						<button
							type="button"
							className="suno-actions-sheet__folders-back"
							onClick={() => setSubView('menu')}
							aria-label="Назад к меню"
						>
							<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
								<polyline points="15 18 9 12 15 6" />
							</svg>
						</button>
						<span className="suno-actions-sheet__folders-title">Добавить в папку</span>
					</div>
					<FolderPickerList
						active={open && subView === 'addToAlbum'}
						itemIds={[track.id]}
						api={albumPickerAPI}
						labels={albumPickerLabels}
						onChanged={onAlbumsChanged!}
						compact
					/>
				</>
			)}

			{/* ── SubView 'menu' — обычный полный набор групп ─────── */}
			{subView === 'menu' && (
				<>

			{/* ── Группа 0: Папки (организация трека) ─────────────── */}
			{showFoldersGroup && (
				<section className="suno-actions-sheet__group suno-actions-sheet__group--folders">
					<h3 className="suno-actions-sheet__group-title">Папки</h3>
					<div className="suno-actions-sheet__list">
						{(() => {
							const albumCount = track.album_ids?.length ?? 0
							return (
								<SideTooltip text={usePopover ? 'Один трек может быть сразу в нескольких папках' : undefined}>
									<button
										type="button"
										className="suno-actions-sheet__row"
										onClick={() => setSubView('addToAlbum')}
									>
										<span className="suno-actions-sheet__row-main">
											<span className="suno-actions-sheet__row-label">Добавить в папку</span>
											<span className="suno-actions-sheet__row-desc">
												{albumCount > 0
													? `В ${albumCount} ${pluralizeAlbums(albumCount)}`
													: 'Положить трек в одну или несколько папок'}
											</span>
										</span>
										<span className="suno-actions-sheet__row-open" aria-hidden="true">→</span>
									</button>
								</SideTooltip>
							)
						})()}
						{onRemoveFromCurrentAlbum && (
							<SideTooltip text={usePopover ? 'Сам трек останется в библиотеке' : undefined}>
								<button
									type="button"
									className="suno-actions-sheet__row suno-actions-sheet__row--danger"
									onClick={() => {
										onRemoveFromCurrentAlbum(track)
										onClose()
									}}
								>
									<span className="suno-actions-sheet__row-main">
										<span className="suno-actions-sheet__row-label">
											{currentAlbumName
												? `Убрать из «${currentAlbumName}»`
												: 'Убрать из этой папки'}
										</span>
										<span className="suno-actions-sheet__row-desc">
											Сам трек останется в библиотеке
										</span>
									</span>
								</button>
							</SideTooltip>
						)}
					</div>
				</section>
			)}

			{/* ── Группа 1: Быстрые (one-click) ─────────────────────── */}
			<section className="suno-actions-sheet__group suno-actions-sheet__group--quick">
				<h3 className="suno-actions-sheet__group-title">Быстро</h3>
				<p className="suno-actions-sheet__group-hint">
					Один клик — результат упадёт в библиотеку.
				</p>
				<div className="suno-actions-sheet__grid">
					{actions.map((a) => {
						// Обложка вынесена в единый вход «Обложка трека»
						// (РЕДАКТИРОВАНИЕ) — не дублируем её в Быстро.
						if (a.key === 'cover') return null
						// Доступность операции провайдеру — из единого реестра (trackOperations).
						if (a.variantKey && !opAvailable(a.variantKey, provider)) return null
						// WAV/Cover/MIDI/Persona — отдельные состояния из jobs-контекстов.
						// Остальные — state хука.
						const isWavKey = a.key === 'wav'
						const isMidiKey = a.key === 'midi'
						const isVideoKey = a.key === 'video'
						const isTimestampedKey = a.key === 'timestamped'
						const isPersonaKey = a.key === 'persona'
						const isStemsKey = a.key === 'stems'
						const isMinusKey = a.key === 'stem_minus'
						const isActive = isWavKey
							? wavIsRunning
							: isMidiKey
								? midiIsRunning
								: isVideoKey
									? videoIsRunning
									: isTimestampedKey
										? timestampedIsRunning
										: isPersonaKey
											? personaIsCreating
											: (isStemsKey || isMinusKey)
												? stemsIsRunning
												: (activeKey === a.key && isRunning)
						const disabled = (isRunning && !isActive)
						const price = priceOf(a.variantKey)
						// Для WAV/MIDI/Video/Timestamped если уже есть кэш — label
						// меняется на «Скачать/Открыть …» без цены. Клик сразу
						// открывает viewer / скачивает.
						const wavReady = isWavKey && !!wavCached && !wavIsRunning
						const midiReady = isMidiKey && !!midiCached && !midiIsRunning
						const videoReady = isVideoKey && !!videoCached && !videoIsRunning
						const timestampedReady = isTimestampedKey && !!timestampedCached && !timestampedIsRunning
						// Готовность — по флагам с бэка, РАЗДЕЛЬНО по виду разбора:
						// общий флаг обещал бы «открыть» там, где сделан другой вид,
						// а клик уходил бы в платный запуск.
						const stemsReady = isStemsKey && !!track.has_stems && !stemsIsRunning
						const minusReady = isMinusKey && !!track.has_minus && !stemsIsRunning
						const personaFirst = isPersonaKey && personas.personas.length === 0 && !personaIsCreating
						const ready = wavReady || midiReady || videoReady || timestampedReady || stemsReady || minusReady
						const label = wavReady
							? 'Скачать WAV'
							: midiReady
								? 'Открыть MIDI'
								: videoReady
									? 'Открыть видео'
									: timestampedReady
										? 'Открыть караоке'
										: stemsReady
											? 'Открыть дорожки'
											: minusReady
												? 'Открыть минусовку'
											: provider === 'mureka' && a.key === 'video' ? 'Лирик-видео' : a.label
						const desc = wavReady
							? 'Уже конвертирован — просто скачать'
							: midiReady
								? 'Уже сгенерирован — открыть viewer'
								: videoReady
									? 'Уже сгенерирован — открыть плеер'
									: timestampedReady
										? 'Уже готов — открыть караоке'
										: stemsReady
											? 'Уже разделён — открыть дорожки'
											: minusReady
												? 'Уже готова — открыть минусовку'
											: personaFirst
												? 'Твоя первая персона — создастся из голоса этого трека'
												: provider === 'mureka' && a.key === 'video' ? 'Караоке-видео с текстом — по всей песне' : a.description
						// В popover-режиме описание скрыто, показываем его
						// через Tooltip на hover. В sheet-режиме описание уже
						// видно inline — tooltip не нужен (text=undefined →
						// passthrough).
						const btn = (
							<button
								key={a.key}
								type="button"
								className={`suno-actions-sheet__btn${isActive ? ' suno-actions-sheet__btn--loading' : ''}${ready ? ' suno-actions-sheet__btn--ready' : ''}`}
								onClick={() => handleQuickAction(a)}
								disabled={disabled || guard.shortFor(price)}
								title={price !== null && guard.shortFor(price) ? guard.titleFor(price) : undefined}
							>
								<span className="suno-actions-sheet__btn-label">{label}</span>
								<span className="suno-actions-sheet__btn-desc">{desc}</span>
								{price !== null && !isActive && !ready && (
									<span className="suno-actions-sheet__btn-price">
										<TokenPrice value={price} iconSize={11} />
									</span>
								)}
								{isActive && (
									<span className="suno-actions-sheet__btn-spinner" aria-hidden="true" />
								)}
							</button>
						)
						return (
							<SideTooltip key={a.key} text={usePopover ? desc : undefined}>
								{btn}
							</SideTooltip>
						)
					})}
					{opAvailable('describe_song', provider) && (() => {
						const dPrice = priceOf('describe_song')
						const dBtn = (
							<button
								type="button"
								className={`suno-actions-sheet__btn${describeBusy ? ' suno-actions-sheet__btn--loading' : ''}`}
								onClick={() => runDescribe(track.id)}
								disabled={describeBusy || guard.shortFor(dPrice)}
								title={dPrice !== null && guard.shortFor(dPrice) ? guard.titleFor(dPrice) : undefined}
							>
								<span className="suno-actions-sheet__btn-label">Анализ</span>
								<span className="suno-actions-sheet__btn-desc">Жанр, настроение, инструменты, описание</span>
								{dPrice !== null && !describeBusy && (
									<span className="suno-actions-sheet__btn-price"><TokenPrice value={dPrice} iconSize={11} /></span>
								)}
								{describeBusy && <span className="suno-actions-sheet__btn-spinner" aria-hidden="true" />}
							</button>
						)
						return <SideTooltip text={usePopover ? 'Жанр, настроение, инструменты, описание' : undefined}>{dBtn}</SideTooltip>
					})()}
				</div>
			</section>
			
			{/* ── Группа 2: Новая версия (параметрическая) ─────────── */}
			{onTrackParametricAction && (
				<section className="suno-actions-sheet__group suno-actions-sheet__group--parametric">
					<h3 className="suno-actions-sheet__group-title">Новая версия</h3>
					<p className="suno-actions-sheet__group-hint">
						Откроет виджет с подставленным треком — допишешь параметры и сгенерируешь.
					</p>
					<div className="suno-actions-sheet__list">
						{PARAMETRIC_ACTIONS.map((p) => {
							const rowPrice = priceOf(p.variantKey)
							return (
								<SideTooltip key={p.operation} text={usePopover ? p.description : undefined}>
									<button
										type="button"
										className="suno-actions-sheet__row"
										onClick={() => handleParametric(p)}
										disabled={isRunning || guard.shortFor(rowPrice)}
										title={rowPrice !== null && guard.shortFor(rowPrice) ? guard.titleFor(rowPrice) : undefined}
									>
										<span className="suno-actions-sheet__row-main">
											<span className="suno-actions-sheet__row-label">{p.label}</span>
											<span className="suno-actions-sheet__row-desc">{p.description}</span>
										</span>
										{rowPrice !== null && (
											<span className="suno-actions-sheet__row-price">
												<TokenPrice value={rowPrice} iconSize={11} />
											</span>
										)}
										<span className="suno-actions-sheet__row-open" aria-hidden="true">→</span>
									</button>
								</SideTooltip>
							)
						})}
					</div>
				</section>
			)}
			
						{/* ── Группа 3: Редактирование (спец UI) ───────────────── */}
						<section className="suno-actions-sheet__group suno-actions-sheet__group--editing">
							<h3 className="suno-actions-sheet__group-title">Редактирование</h3>
							<p className="suno-actions-sheet__group-hint">
			Специальные инструменты со своим интерфейсом.
							</p>
							<div className="suno-actions-sheet__list">
				{hasAudio && opAvailable('replace_section', provider) && (() => {
					const rowPrice = priceOf('replace_section')
					return (
						<SideTooltip text={usePopover ? 'Выдели часть трека и перегенерируй её' : undefined}>
							<button
								type="button"
								className="suno-actions-sheet__row"
								onClick={() => setReplaceOpen(true)}
								disabled={isRunning}
							>
								<span className="suno-actions-sheet__row-main">
									<span className="suno-actions-sheet__row-label">Заменить фрагмент</span>
									<span className="suno-actions-sheet__row-desc">
										Выдели часть трека и перегенерируй её
									</span>
								</span>
								{rowPrice !== null && (
									<span className="suno-actions-sheet__row-price">
										<TokenPrice value={rowPrice} iconSize={11} />
									</span>
								)}
								<span className="suno-actions-sheet__row-open" aria-hidden="true">→</span>
							</button>
						</SideTooltip>
					)
				})()}
				{opAvailable('mashup', provider) && (() => {
					const rowPrice = priceOf('mashup')
					return (
						<SideTooltip text={usePopover ? 'Смиксовать с другим треком из библиотеки' : undefined}>
							<button
								type="button"
								className="suno-actions-sheet__row"
								onClick={() => setMashupOpen(true)}
								disabled={isRunning}
							>
								<span className="suno-actions-sheet__row-main">
									<span className="suno-actions-sheet__row-label">Mashup</span>
									<span className="suno-actions-sheet__row-desc">
										Смиксовать с другим треком из библиотеки
									</span>
								</span>
								{rowPrice !== null && (
									<span className="suno-actions-sheet__row-price">
										<TokenPrice value={rowPrice} iconSize={11} />
									</span>
								)}
								<span className="suno-actions-sheet__row-open" aria-hidden="true">→</span>
							</button>
						</SideTooltip>
					)
				})()}
				{/* Обложка через gpt-image-2 — инлайн-редактор (загрузить/сгенерить/
				    улучшить), тот же что у голосов. Универсально (в т.ч. Mureka, у
				    которой своих обложек нет). Fallback (без onEditCover) — старый
				    переход на страницу инструмента. */}
				<SideTooltip text={usePopover ? 'Загрузить, сгенерировать или улучшить обложку' : undefined}>
					<button
						type="button"
						className="suno-actions-sheet__row"
						onClick={() => {
							onClose()
							if (onEditCover) onEditCover(track)
							else navigate(`/bots/tools/covers?track=${track.id}`)
						}}
					>
						<span className="suno-actions-sheet__row-main">
							<span className="suno-actions-sheet__row-label">Обложка трека</span>
							<span className="suno-actions-sheet__row-desc">
								Загрузить, сгенерировать или улучшить обложку
							</span>
						</span>
						<span className="suno-actions-sheet__row-open" aria-hidden="true">→</span>
					</button>
				</SideTooltip>
				{/* Ранее здесь был «Загрузить свою обложку» — перенесён в
				    группу «Обложка» в Быстро (picker AI/Upload), по модели
				    Suno.com где управление обложкой — единая точка. */}
			</div>
				</section>

			{/* ── Группа 4: Удаление трека (без угрожающего «ОПАСНАЯ ЗОНА»
			    заголовка — лишний стресс). Компактный ряд: кнопка + `ⓘ`
			    с tooltip'ом о последствиях удаления. */}
			<section className="suno-actions-sheet__group suno-actions-sheet__group--danger">
				{!confirmDelete ? (
					<div className="suno-actions-sheet__delete-row">
						<button
							type="button"
							className="suno-actions-sheet__delete-btn"
							onClick={() => setConfirmDelete(true)}
							disabled={deleting || isRunning}
						>
							<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
								<polyline points="3 6 5 6 21 6" />
								<path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
								<path d="M10 11v6" />
								<path d="M14 11v6" />
								<path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
							</svg>
							<span>Удалить трек</span>
						</button>
						<SideTooltip
							text="Удаление необратимо. Трек исчезнет из библиотеки и всех папок."
							maxWidth={260}
						>
							<button
								type="button"
								className="suno-actions-sheet__delete-info"
								aria-label="Что произойдёт при удалении?"
							>
								<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
									<circle cx="12" cy="12" r="10" />
									<line x1="12" y1="16" x2="12" y2="12" />
									<line x1="12" y1="8" x2="12.01" y2="8" />
								</svg>
							</button>
						</SideTooltip>
					</div>
				) : (
					<div className="suno-actions-sheet__confirm">
						<p className="suno-actions-sheet__confirm-text">
							Удалить трек «{track.title?.trim() || 'Без названия'}»?
							Трек исчезнет из библиотеки и всех папок — отменить нельзя.
						</p>
						<div className="suno-actions-sheet__confirm-actions">
							<button
								type="button"
								className="suno-actions-sheet__delete-btn suno-actions-sheet__delete-btn--solid"
								onClick={handleDeleteTrack}
								disabled={deleting}
							>
								{deleting ? 'Удаляем…' : 'Да, удалить'}
							</button>
							<button
								type="button"
								className="suno-actions-sheet__confirm-cancel"
								onClick={() => setConfirmDelete(false)}
								disabled={deleting}
							>
								Отмена
							</button>
						</div>
					</div>
				)}
			</section>
				</>
			)}
		</div>
	)

	const handleOpenReset = () => {
		// При любом открытии — сбрасываем confirm-стадию. Иначе если юзер
		// закрыл sheet в состоянии «подтвердите удаление» и открыл снова,
		// увидит страшный confirm сразу, что сбивает с толку.
		setConfirmDelete(false)
	}

	return (
		<>
			{usePopover && anchorEl ? (
				<AnchoredPopover
					open={open}
					onClose={() => { handleOpenReset(); onClose() }}
					anchorEl={anchorEl}
					width={POPOVER_WIDTH}
					align="center"
					className="suno-actions-sheet suno-actions-sheet--popover"
				>
					{body}
				</AnchoredPopover>
			) : (
				<Sheet
					open={open}
					onClose={() => { handleOpenReset(); onClose() }}
					variant="center"
					title="Работать с треком"
					className="suno-actions-sheet"
				>
					{body}
				</Sheet>
			)}

			<SunoResultViewer
				open={!!viewer}
				onClose={() => setViewer(null)}
				actionKey={viewer?.key ?? null}
				result={viewer?.result ?? null}
				sourceTrack={track}
			/>

			{describeData && <DescribeModal data={describeData} onClose={closeDescribe} />}

			<SunoReplaceSectionSheet
				track={track}
				open={replaceOpen}
				onClose={() => setReplaceOpen(false)}
				onCompleted={onCompleted}
			/>

			<SunoMashupSheet
				track={track}
				open={mashupOpen}
				onClose={() => setMashupOpen(false)}
				onCompleted={onCompleted}
				tracksCache={tracksCache}
			/>

			{/* Тур поверх popover/sheet. Через createPortal к document.body —
			    чтобы SpotlightTour (position:fixed) не прилипал к containing
			    block'у (transform/filter на ancestor'ах) и гарантированно был
			    над Sheet/popover. z-index 10100+ из _spotlight-tour.scss. */}
			{open && tourOpen && createPortal(
				<SpotlightTour
					steps={buildActionsSheetTourSteps()}
					storageKey={SUNO_ACTIONS_SHEET_TOUR_KEY}
					onFinish={handleTourFinish}
				/>,
				document.body,
			)}
		</>
	)
}
